class MobileDock extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const header = document.querySelector('.shopify-section-header');

    this.isAction = false;
    this.isCompact = false;
    this.ticking = false;
    this.idleTimer = null;
    this.clearance = 0;
    this.lastScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.revealY = header ? parseInt(header.getBoundingClientRect().bottom) : 0;

    if (window.Shopify && Shopify.designMode) {
      this.revealY = 0;
    }

    this.querySelectorAll('.dock__item[data-action]').forEach((button) => {
      button.addEventListener('click', this.onButtonClick.bind(this));
    });
    document.addEventListener('menudrawer:close', this.hideStickyHeader.bind(this));
    document.addEventListener('searchmodal:close', this.hideStickyHeader.bind(this));

    this.onScrollHandler = this.onScroll.bind(this);
    this.onResizeHandler = this.measure.bind(this);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    window.addEventListener('resize', this.onResizeHandler, { passive: true });
    document.addEventListener('matchSmall', this.onResizeHandler);

    this.onScrollHandler();
    this.measure();

    // Third-party launchers (chat, WhatsApp, …) are injected asynchronously,
    // so re-check a few times instead of only on load.
    window.addEventListener('load', () => this.liftFloatingWidgets());
    this.retryTimers = [
      setTimeout(() => this.liftFloatingWidgets(), 1500),
      setTimeout(() => this.liftFloatingWidgets(), 4000)
    ];

    if ('MutationObserver' in window) {
      this.observer = new MutationObserver(() => {
        clearTimeout(this.observerTimer);
        this.observerTimer = setTimeout(() => this.liftFloatingWidgets(), 400);
      });
      this.observer.observe(document.body, { childList: true });
      setTimeout(() => this.observer && this.observer.disconnect(), 20000);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
    window.removeEventListener('resize', this.onResizeHandler);
    document.removeEventListener('matchSmall', this.onResizeHandler);
    clearTimeout(this.idleTimer);
    clearTimeout(this.observerTimer);
    (this.retryTimers || []).forEach(clearTimeout);
    if (this.observer) this.observer.disconnect();
  }

  /* ----------------------------------------------------------------
     Sizing
     ---------------------------------------------------------------- */

  // Page padding and launcher clearance are both derived from the EXPANDED
  // pill, so nothing shifts when it compacts on scroll.
  measure() {
    if (this.isCompact) return;

    const height = this.offsetHeight;
    if (!height) return;

    document.documentElement.style.setProperty('--mobile-dock-height', `${height}px`);
    this.expandedHeight = height;
    this.liftFloatingWidgets();
  }

  /* ----------------------------------------------------------------
     Scroll: reveal, then compact while moving down, expand when idle
     ---------------------------------------------------------------- */

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;

    window.requestAnimationFrame(() => {
      this.ticking = false;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollTop >= this.revealY) {
        this.classList.add('is-active');
      } else {
        this.classList.remove('is-active');
      }

      const delta = scrollTop - this.lastScroll;

      if (Math.abs(delta) > 4) {
        this.lastScroll = scrollTop;
        // Down = compact. Up = back to full size straight away.
        this.setCompact(delta > 0 && scrollTop > 48);
      }

      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => this.setCompact(false), 220);
    });
  }

  setCompact(compact) {
    if (compact === this.isCompact) return;
    this.isCompact = compact;
    this.classList.toggle('is-compact', compact);
  }

  /* ----------------------------------------------------------------
     Keep floating launchers (chat bubbles) off the pill
     ---------------------------------------------------------------- */

  liftFloatingWidgets() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const height = this.expandedHeight || this.offsetHeight;
    if (!height) return;

    const styles = window.getComputedStyle(this);
    const bottom = parseFloat(styles.bottom) || 12;
    const clearance = Math.round(height + bottom + 12);

    if (clearance === this.clearance) return;
    this.clearance = clearance;
    document.documentElement.style.setProperty('--mobile-dock-clearance', `${clearance}px`);

    const dockRect = this.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    Array.prototype.forEach.call(document.body.children, (el) => {
      if (el === this || el.contains(this)) return;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE' || el.tagName === 'LINK') return;

      let style;
      try {
        style = window.getComputedStyle(el);
      } catch (error) {
        return;
      }

      if (style.position !== 'fixed') return;
      if (style.display === 'none' || style.visibility === 'hidden') return;

      const rect = el.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      // Only small floating things — never a full-width bar or an open panel.
      if (rect.width > vw * 0.6 || rect.height > vh * 0.5) return;
      // Already clear of the pill?
      if (rect.bottom <= dockRect.top + 4) return;
      // No horizontal overlap with the pill?
      if (rect.right < dockRect.left || rect.left > dockRect.right) return;

      el.setAttribute('data-dock-lifted', '');
      el.style.setProperty('bottom', 'var(--mobile-dock-clearance)', 'important');
    });
  }

  /* ----------------------------------------------------------------
     Actions (unchanged)
     ---------------------------------------------------------------- */

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;
    const delay = this.waitStickyHeader();

    switch (target.dataset.action) {
      case 'cart':
        this.isAction = true;

        const miniCart = document.querySelector('mini-cart');
        if (miniCart) {
          setTimeout(() => {
            miniCart.open();
            document.activeElement.blur();
          }, delay);
        }
        else {
          window.location.href = window.routes.cart_url;
        }
        break;

      case 'menu':
        this.isAction = true;

        const headerDrawer = document.querySelector('header-drawer');
        if (headerDrawer) {
          setTimeout(() => {
            headerDrawer.openMenuDrawer();
          }, delay);
        }
        break;

      case 'search':
        this.isAction = true;

        const searchModals = document.querySelectorAll('search-modal');
        if (searchModals.length > 0) {
          searchModals.forEach((searchModal) => {
            const style = window.getComputedStyle(searchModal);
            if (style.display === 'none') {
              return;
            }

            setTimeout(() => {
              searchModal.open();
              searchModal.querySelector('input:not([type="hidden"])').focus();
            }, delay);
          });
        }
        else {
          window.location.href = window.routes.search_url;
        }
        break;
    }
  }

  hideStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (header === null) return;

    if (theme.config.mqlSmall && this.isAction && header.sticky()) {
      setTimeout(() => {
        header.hide();
        this.isAction = false;
      }, 500);
    }
  }

  waitStickyHeader() {
    const header = document.querySelector('sticky-header');
    if (header === null) return;

    if (!header.sticky() && !this.isElementVisible(header)) {
      header.hide();
      setTimeout(() => header.reveal());

      return 250;
    }

    return 0;
  }

  isElementVisible(element) {
    const rect = element.getBoundingClientRect(),
        width  = window.innerWidth || document.documentElement.clientWidth,
        height = window.innerHeight || document.documentElement.clientHeight,
        efp    = function (x, y) { return document.elementFromPoint(x, y) };

    // Return false if it's not in the viewport
    if (rect.right < 0 || rect.bottom < 0 || rect.left > width || rect.top > height) {
      return false;
    }

    // Return true if any of its four corners are visible
    return (
      element.contains(efp(rect.left,  rect.top))
      || element.contains(efp(rect.right, rect.top))
      || element.contains(efp(rect.right, rect.bottom))
      || element.contains(efp(rect.left,  rect.bottom))
      || element.parentNode.contains(efp(rect.left,  rect.top))
      || element.parentNode.contains(efp(rect.right, rect.top))
      || element.parentNode.contains(efp(rect.right, rect.bottom))
      || element.parentNode.contains(efp(rect.left,  rect.bottom))
    );
  }
}
customElements.define('mobile-dock', MobileDock);
